import { useEffect, useState, type FormEvent } from 'react'
import {
  Money as Cash,
  Bank,
} from '@phosphor-icons/react'
import { Modal } from './Modal'
import { supabase } from '@/lib/supabase'
import { formatQ } from '@/lib/format'
import type {
  Caja,
  CuentaBancariaPropia,
  FormaPago,
  FormaPagoPayload,
  InscripcionResult,
} from '@/lib/typesNeoCore'
import { imprimirReciboIngresos, imprimirWelcomeCard } from '@/lib/imprimibles'
import { hoyGT } from '../lib/fechas'

/**
 * Modal de inscripción del prospecto.
 *
 * Flujo crítico de NeoCore: cobra cuota de ingreso + aportación inicial,
 * activa al asociado, crea sus cuentas reales (aportaciones + ahorro
 * corriente) y registra los productos en el FEIS — todo en una transacción
 * via RPC `registrar_pago_inscripcion`.
 *
 * UX simplificada (Hugo, abril 2026):
 *   - La caja se detecta sola (siempre la caja física activa del NeoCore).
 *     La caja virtual es para apps móviles, no aplica acá.
 *   - Solo 2 formas de pago: efectivo (default) o depósito bancario.
 *     No combinamos por default — se reemplaza al cambiar.
 *   - No hay cheque ni transferencia de tercero (no se usan en inscripción).
 */

type Props = {
  open: boolean
  onClose: () => void
  asociadoId: string
  nombreCompleto: string
  noAsociado: string
  neoId?: string
  dpi?: string
  onSuccess: (r: InscripcionResult) => void
}

type FormaSimple = Extract<FormaPago, 'efectivo' | 'deposito'>

export function RegistrarInscripcionModal({
  open, onClose, asociadoId, nombreCompleto, noAsociado, neoId, dpi, onSuccess,
}: Props) {
  // Catálogos
  const [caja, setCaja] = useState<Caja | null>(null)
  const [cuentasBanco, setCuentasBanco] = useState<CuentaBancariaPropia[]>([])
  const [montoCuota, setMontoCuota] = useState(50)
  const [montoAportacion, setMontoAportacion] = useState(50)
  const [loadingCat, setLoadingCat] = useState(true)
  const [errorCat, setErrorCat] = useState<string | null>(null)

  // UI: una sola forma de pago activa
  const [formaPago, setFormaPago] = useState<FormaSimple>('efectivo')

  // Datos del depósito (solo si formaPago === 'deposito')
  const [cuentaBancariaId, setCuentaBancariaId] = useState<string>('')
  const [fechaBoleta, setFechaBoleta] = useState<string>(hoyGT())
  const [numeroBoleta, setNumeroBoleta] = useState<string>('')
  const [montoBoletaTotal, setMontoBoletaTotal] = useState<string>('')

  // Submit
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<InscripcionResult | null>(null)

  const montoTotal = montoCuota + montoAportacion

  // Cargar catálogos al abrir
  useEffect(() => {
    if (!open) return
    setLoadingCat(true)
    setErrorCat(null)
    setSuccess(null)
    setError(null)
    setFormaPago('efectivo')
    setCuentaBancariaId('')
    setNumeroBoleta('')
    setMontoBoletaTotal('')

    Promise.all([
      // Caja física activa (en NeoCore es la principal)
      supabase.from('caja').select('*').eq('activa', true).eq('tipo', 'FISICA').limit(1),
      supabase.from('cuenta_bancaria_propia').select('*').eq('activa', true).order('alias', { ascending: true }),
      supabase.from('parametro_sistema').select('clave, valor')
        .in('clave', ['aportacion.cuota_ingreso', 'aportacion.monto_inicial']),
    ]).then(([cajasResp, ctasResp, paramResp]) => {
      if (cajasResp.error) { setErrorCat(cajasResp.error.message); setLoadingCat(false); return }
      if (ctasResp.error)  { setErrorCat(ctasResp.error.message);  setLoadingCat(false); return }

      const cajaActiva = (cajasResp.data as Caja[])[0] ?? null
      if (!cajaActiva) {
        setErrorCat('No hay caja física activa. Andá a Caja → abrir caja antes de cobrar.')
        setLoadingCat(false); return
      }
      setCaja(cajaActiva)
      setCuentasBanco(ctasResp.data as CuentaBancariaPropia[])

      // Pre-cargar montos
      const ps = (paramResp.data ?? []) as Array<{ clave: string; valor: string | null }>
      const cuota = Number(ps.find((p) => p.clave === 'aportacion.cuota_ingreso')?.valor ?? 50)
      const aporte = Number(ps.find((p) => p.clave === 'aportacion.monto_inicial')?.valor ?? 50)
      setMontoCuota(cuota)
      setMontoAportacion(aporte)

      setLoadingCat(false)
    })
  }, [open])

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)

    if (!caja) { setError('No hay caja física activa.'); return }

    // Construir el array `pagos` con UNA sola forma cubriendo el total
    const pagos: FormaPagoPayload[] = []

    if (formaPago === 'efectivo') {
      pagos.push({ tipo: 'efectivo', monto: montoTotal })
    } else {
      // depósito — validamos sub-form
      if (!cuentaBancariaId) { setError('Seleccioná la cuenta bancaria de destino.'); return }
      if (!numeroBoleta.trim()) { setError('Falta el número de boleta.'); return }
      if (!fechaBoleta) { setError('Falta la fecha de la boleta.'); return }
      const totalBoleta = Number(montoBoletaTotal)
      if (!totalBoleta || totalBoleta <= 0) { setError('Falta el monto total de la boleta.'); return }
      if (totalBoleta < montoTotal) {
        setError(`El monto total de la boleta (${formatQ(totalBoleta)}) no puede ser menor al monto a cobrar (${formatQ(montoTotal)}).`)
        return
      }

      pagos.push({
        tipo: 'deposito',
        monto: montoTotal,
        cuenta_bancaria_propia_id: cuentaBancariaId,
        fecha_boleta: fechaBoleta,
        numero_boleta: numeroBoleta.trim(),
        monto_boleta_total: totalBoleta,
      })
    }

    setSubmitting(true)
    const { data, error: rpcErr } = await supabase.rpc('registrar_pago_inscripcion', {
      p_input: { asociado_id: asociadoId, caja_id: caja.id, pagos },
    })
    setSubmitting(false)

    if (rpcErr) { setError(`No se pudo registrar: ${rpcErr.message}`); return }
    setSuccess(data as InscripcionResult)
    onSuccess(data as InscripcionResult)
  }

  // Vista de éxito
  if (success) {
    const onImprimirRecibo = async () => {
      // Fetch correlativo + datos del movimiento + cajero + verification_code (QR)
      const { data: mov } = await supabase
        .from('movimiento_caja')
        .select(`
          correlativo, fecha, hora, verification_code,
          serie:serie_documento_id (codigo),
          pagos:movimiento_caja_pago (
            tipo, monto,
            boleta:boleta_bancaria_id (
              fecha_boleta, numero_boleta,
              cuenta:cuenta_bancaria_propia_id (banco, numero_cuenta)
            )
          )
        `)
        .eq('id', success.movimiento_caja_id)
        .single()

      if (!mov) return

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const m = mov as any
      const sessionData = (await supabase.auth.getSession()).data.session
      const cajero = sessionData?.user?.email ?? 'Cajero'
      const pago = m.pagos[0]
      const boleta = pago?.boleta
      const cta = boleta?.cuenta

      imprimirReciboIngresos({
        serie: m.serie?.codigo ?? '',
        correlativo: m.correlativo,
        fecha: `${m.fecha}T${m.hora}`,
        asociado_no: noAsociado,
        neo_id: neoId,
        asociado_nombre: nombreCompleto,
        asociado_dpi: dpi ?? '',
        concepto: 'Inscripción asociado',
        // Desglose tabular: dos operaciones en una para que se vea sin fricción
        desglose: [
          { label: 'Cuota de ingreso',   monto: montoCuota },
          { label: 'Aportación inicial', monto: montoAportacion },
        ],
        monto: success.monto_total,
        forma_pago: pago?.tipo ?? 'efectivo',
        cajero,
        banco: cta?.banco,
        numero_cuenta: cta?.numero_cuenta,
        numero_boleta: boleta?.numero_boleta,
        fecha_boleta: boleta?.fecha_boleta,
        verification_code: m.verification_code,
      })
    }

    const onImprimirWelcome = () => {
      imprimirWelcomeCard({
        asociado_no: noAsociado,
        neo_id: neoId,
        asociado_nombre: nombreCompleto,
        fecha_ingreso: hoyGT(),
      })
    }

    const primerNombre = nombreCompleto.trim().split(/\s+/)[0] ?? ''
    const primerNombreCap = primerNombre.charAt(0) + primerNombre.slice(1).toLowerCase()
    const neoIdDisplay = neoId
      ? `NEO ${neoId.slice(0, 3)} ${neoId.slice(3, 6)}`
      : `NEO ${noAsociado.replace(/\D/g, '').slice(-6).slice(0, 3)} ${noAsociado.replace(/\D/g, '').slice(-6).slice(3, 6)}`

    return (
      <Modal
        open={open}
        onClose={onClose}
        title={`¡Bienvenido a Neocoop, ${primerNombreCap}!`}
        subtitle={`Acabás de sumar a ${nombreCompleto} como miembro oficial`}
        size="md"
        footer={
          <button type="button" className="btn btn-primary" onClick={onClose} style={{ minWidth: 120 }}>
            Cerrar
          </button>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          {/* HERO ASOCIADO — protagonista, mismo lenguaje del comprobante */}
          <div
            style={{
              position: 'relative',
              padding: '20px 22px',
              background: 'var(--surface)',
              border: '1px solid var(--hairline)',
              borderRadius: 14,
              overflow: 'hidden',
            }}
          >
            <img
              src="/brand/isotipo-naranja.svg"
              alt=""
              aria-hidden="true"
              style={{
                position: 'absolute',
                right: -28, bottom: -28,
                width: 130, height: 130,
                opacity: 0.06,
                pointerEvents: 'none',
              }}
            />
            <div style={{ position: 'relative', zIndex: 1 }}>
              <div style={{ fontSize: 9.5, fontWeight: 700, color: 'var(--muted-foreground)', letterSpacing: 2, textTransform: 'uppercase' }}>
                Bienvenido a la familia
              </div>
              <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--ink)', letterSpacing: -0.6, lineHeight: 1.15, marginTop: 4 }}>
                {nombreCompleto}
              </div>
              <div style={{ marginTop: 8, fontFamily: 'var(--font-mono)', fontSize: 12.5, fontWeight: 600, letterSpacing: 1.4, color: 'var(--brand)' }}>
                {neoIdDisplay}
              </div>
            </div>
          </div>

          {/* Desglose del cobro estilo recibo (key-value minimal) */}
          <div>
            <div className="kv-min"><span>Cuota de ingreso</span><span>Q {montoCuota.toFixed(2)}</span></div>
            <div className="kv-min"><span>Aportación inicial</span><span>Q {montoAportacion.toFixed(2)}</span></div>
            <div className="kv-min total"><span>Total recibido hoy</span><span>Q {success.monto_total.toFixed(2)}</span></div>
          </div>

          {/* Cuentas creadas */}
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--muted-foreground)', letterSpacing: 1.2, textTransform: 'uppercase', marginBottom: 8 }}>
              Cuentas creadas
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <CuentaPill label="Aportaciones" numero={success.cuenta_aportaciones_numero} saldo={formatQ(montoAportacion)} />
              <CuentaPill label="Ahorro corriente" numero={success.cuenta_ahorro_numero} saldo="Q 0.00" />
            </div>
          </div>

          {/* CTAs principales — comprobante + pase de bienvenida */}
          <div style={{ marginTop: 4 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--muted-foreground)', letterSpacing: 1.2, textTransform: 'uppercase', marginBottom: 8 }}>
              Generar documentos
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <button type="button" onClick={onImprimirRecibo} className="cta-doc primary">
                <span className="cta-label">Comprobante</span>
                <svg className="cta-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12h14M13 5l7 7-7 7"/>
                </svg>
              </button>
              <button type="button" onClick={onImprimirWelcome} className="cta-doc primary">
                <span className="cta-label">Pase de bienvenida</span>
                <svg className="cta-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12h14M13 5l7 7-7 7"/>
                </svg>
              </button>
            </div>
          </div>

          <style>{`
            .kv-min {
              display: flex; justify-content: space-between; align-items: center;
              padding: 7px 0;
              font-size: 13px;
              color: var(--ink-soft);
              border-bottom: 1px solid var(--hairline);
            }
            .kv-min:last-child { border-bottom: 0; }
            .kv-min span:last-child {
              color: var(--ink); font-weight: 600;
              font-feature-settings: "tnum"; font-variant-numeric: tabular-nums;
            }
            .kv-min.total {
              padding-top: 12px;
              border-top: 1px solid var(--hairline);
              border-bottom: 0;
              font-weight: 700;
              color: var(--ink);
            }
            .kv-min.total span:last-child {
              color: var(--brand); font-weight: 800;
            }

            .cta-doc {
              all: unset;
              cursor: pointer;
              display: flex; align-items: center; justify-content: space-between;
              gap: 10px;
              padding: 14px 16px;
              border-radius: 12px;
              transition: transform 120ms ease, box-shadow 140ms ease, background 140ms ease;
              font-family: inherit;
            }
            .cta-doc.primary {
              background: var(--brand);
              color: #fff;
              box-shadow: var(--shadow-brand);
            }
            .cta-doc.primary:hover {
              background: var(--brand-deep);
              transform: translateY(-1px);
              box-shadow: 0 12px 28px rgba(255,91,4,0.28);
            }
            .cta-doc.primary:active {
              transform: translateY(0);
              box-shadow: var(--shadow-brand);
            }
            .cta-label {
              font-size: 13px; font-weight: 700;
              letter-spacing: -0.2px;
            }
            .cta-arrow {
              width: 16px; height: 16px;
              flex-shrink: 0;
              opacity: 0.85;
              transition: transform 140ms ease;
            }
            .cta-doc.primary:hover .cta-arrow {
              transform: translateX(3px);
              opacity: 1;
            }
          `}</style>
        </div>
      </Modal>
    )
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Registrar pago de inscripción"
      subtitle={`${nombreCompleto} · Asociado ${noAsociado}`}
      size="md"
      blocking={submitting}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={submitting}>
            Cancelar
          </button>
          <button type="submit" form="inscripcion-form" className="btn btn-primary" disabled={submitting || loadingCat || !!errorCat}>
            {submitting ? 'Procesando…' : `Cobrar y activar · ${formatQ(montoTotal)}`}
          </button>
        </>
      }
    >
      {loadingCat ? (
        <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)' }}>Cargando…</div>
      ) : errorCat ? (
        <div className="error-banner">{errorCat}</div>
      ) : (
        <form id="inscripcion-form" onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          {error && <div className="error-banner" style={{ margin: 0 }}>{error}</div>}

          {/* RESUMEN del cobro */}
          <div style={{ background: 'var(--brand-50)', border: '1px solid var(--brand-100, var(--brand-200))', borderRadius: 12, padding: '14px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--brand-700)', letterSpacing: 0.5, textTransform: 'uppercase' }}>
                Pago de inscripción
              </span>
              <div style={{ display: 'flex', gap: 14, fontSize: 12, color: 'var(--text-secondary)', flexWrap: 'wrap' }}>
                <span>Cuota de ingreso: <strong>Q {montoCuota.toFixed(2)}</strong></span>
                <span>+ Aportación inicial: <strong>Q {montoAportacion.toFixed(2)}</strong></span>
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: 0 }}>Total a cobrar</p>
              <p style={{ fontSize: 26, fontWeight: 700, margin: '2px 0 0', color: 'var(--brand-700)', fontFeatureSettings: '"tnum"' }}>
                Q {montoTotal.toFixed(2)}
              </p>
            </div>
          </div>

          {/* CAJA — informativo, no editable */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--text-muted)' }}>
            <span>Caja:</span>
            <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
              {caja?.codigo} · {caja?.nombre}
            </span>
          </div>

          {/* FORMA DE PAGO — segmented control */}
          <div>
            <label className="form-label" style={{ marginBottom: 8, display: 'block' }}>Forma de pago</label>
            <div
              role="radiogroup"
              style={{
                display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8,
              }}
            >
              <FormaTab
                Icon={Cash}
                label="Efectivo"
                hint="El asociado paga en billetes en caja"
                selected={formaPago === 'efectivo'}
                onClick={() => setFormaPago('efectivo')}
              />
              <FormaTab
                Icon={Bank}
                label="Depósito bancario"
                hint="El asociado depositó en una cuenta de la coop"
                selected={formaPago === 'deposito'}
                onClick={() => setFormaPago('deposito')}
              />
            </div>
          </div>

          {/* Sub-form depósito */}
          {formaPago === 'deposito' && (
            <div
              style={{
                background: 'var(--surface)', border: '1px solid var(--border)',
                borderRadius: 10, padding: '14px 16px',
                display: 'flex', flexDirection: 'column', gap: 12,
              }}
            >
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Datos de la boleta
              </div>

              {cuentasBanco.length === 0 ? (
                <div className="error-banner" style={{ margin: 0, fontSize: 12 }}>
                  No hay cuentas bancarias registradas. Andá a <strong>Bancos</strong> en el menú lateral para agregar al menos una.
                </div>
              ) : (
                <>
                  <SubField label="Cuenta destino">
                    <select value={cuentaBancariaId} onChange={(e) => setCuentaBancariaId(e.target.value)}>
                      <option value="">— Elegir cuenta —</option>
                      {cuentasBanco.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.alias ?? c.banco} · {c.numero_cuenta} ({c.tipo.toLowerCase()} {c.moneda})
                        </option>
                      ))}
                    </select>
                  </SubField>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10 }}>
                    <SubField label="Fecha de la boleta">
                      <input type="date" value={fechaBoleta} onChange={(e) => setFechaBoleta(e.target.value)} />
                    </SubField>
                    <SubField label="N° boleta">
                      <input value={numeroBoleta} onChange={(e) => setNumeroBoleta(e.target.value)} placeholder="9335368" />
                    </SubField>
                    <SubField label="Monto total boleta (Q)">
                      <input
                        type="number" min={montoTotal} step="0.01"
                        value={montoBoletaTotal}
                        onChange={(e) => setMontoBoletaTotal(e.target.value)}
                        placeholder={montoTotal.toFixed(2)}
                      />
                    </SubField>
                  </div>

                  <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: 0, lineHeight: 1.5 }}>
                    Si el monto de la boleta es mayor a Q {montoTotal.toFixed(2)}, el saldo queda
                    abierto para usar en otra transacción del asociado.
                  </p>
                </>
              )}
            </div>
          )}
        </form>
      )}
    </Modal>
  )
}

// =============================================================
// Componentes internos
// =============================================================

function FormaTab({
  Icon, label, hint, selected, onClick,
}: {
  Icon: typeof Cash
  label: string
  hint: string
  selected: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      onClick={onClick}
      style={{
        all: 'unset', cursor: 'pointer',
        display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 6,
        padding: '14px 16px',
        background: selected ? 'var(--brand-50)' : 'var(--surface)',
        border: `1.5px solid ${selected ? 'var(--brand-600)' : 'var(--border)'}`,
        borderRadius: 10,
        boxShadow: selected ? '0 4px 12px rgba(255,91,4,0.10)' : 'none',
        transition: 'all 120ms ease',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{
          width: 28, height: 28, borderRadius: 7,
          background: selected ? 'var(--brand-600)' : 'var(--n-100)',
          color: selected ? '#fff' : 'var(--text-muted)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Icon size={14} weight="duotone" />
        </span>
        <span style={{ fontSize: 13, fontWeight: 700, color: selected ? 'var(--brand-700)' : 'var(--text-primary)' }}>
          {label}
        </span>
      </div>
      <span style={{ fontSize: 11, color: 'var(--text-muted)', lineHeight: 1.4 }}>
        {hint}
      </span>
    </button>
  )
}

function SubField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.4 }}>
        {label}
      </span>
      {children}
    </label>
  )
}

function CuentaPill({ label, numero, saldo }: { label: string; numero: string; saldo: string }) {
  return (
    <div
      style={{
        background: 'var(--surface)', border: '1px solid var(--border)',
        borderRadius: 10, padding: '10px 12px',
      }}
    >
      <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {label}
      </div>
      <div style={{ fontSize: 11, fontFamily: 'var(--font-mono)', color: 'var(--text-primary)', marginTop: 3, fontWeight: 600 }}>
        {numero}
      </div>
      <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--brand-700)', marginTop: 4, fontFeatureSettings: '"tnum"' }}>
        {saldo}
      </div>
    </div>
  )
}
